import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { PrintProduct } from "../../../CheckoutPage/components/types";
import { useEscapeKey } from "../../../../utils/useEscapeKey";
import DiscountTierPanel from "./DiscountTierPanel";

const TruckIcon = () => (
  <svg width="14" height="12" viewBox="0 0 24 18" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M1 1h14v11H1V1zm14 3h4l3 4v4h-7V4zM5.5 16a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm11 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" stroke="#30053f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface PrintProductZoomDrawerProps {
  product: PrintProduct;
  isDescriptionExpanded: boolean;
  showSeeMore: boolean;
  onToggleDescription: () => void;
  onClose: () => void;
}

const DISCLAIMER =
  "Final print results may vary depending on image size and dimensions. Cropped images are centered, which may remove edge portions of the photo.";

const PrintProductZoomDrawer: React.FC<PrintProductZoomDrawerProps> = ({
  product,
  isDescriptionExpanded,
  showSeeMore,
  onToggleDescription,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const closingRef = useRef(false);

  // Animate in after first paint; lock body scroll
  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => setIsVisible(true));
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(id);
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    panelRef.current?.focus();
    if (imgRef.current?.complete) setImageLoaded(true);
  }, []);

  const handleClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  useEscapeKey(handleClose);

  const previewSrc = product.previewImageURL || product.previewImageThumbnailURL;

  return ReactDOM.createPortal(
    <div
      className={`print-zoom-overlay${isVisible ? " print-zoom-overlay--visible" : ""}`}
      onClick={handleClose}
    >
      <div
        ref={panelRef}
        className={`print-zoom-panel${isVisible ? " print-zoom-panel--visible" : ""}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Preview ${product.title}`}
        tabIndex={-1}
      >
        <div
          className={`print-zoom-header${(product.discountTiers.length > 0 || product.freeShipping) ? " print-zoom-header--no-border" : ""}`}
        >
          <div className="print-zoom-header-info">
            <span className="print-zoom-header-title">{product.title}</span>
            <span className="print-zoom-header-price">{product.totalPriceFormatted}</span>
          </div>
          <button
            className="print-zoom-close-btn"
            onClick={handleClose}
            aria-label="Close preview"
          >
            CLOSE
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {product.freeShipping && (
          <div className="print-zoom-free-shipping-banner">
            <TruckIcon />
            <span><strong>Free shipping:</strong> Makes all print products shipping free</span>
          </div>
        )}

        <div className="print-zoom-body">
          {product.discountTiers.length > 0 && (
            <DiscountTierPanel
              tiers={product.discountTiers}
              className="print-zoom-discount-panel"
              badgeClassName="print-zoom-discount-badge"
              title={`${product.title} prints`}
              titleClassName="print-zoom-discount-title"
              tiersClassName="print-zoom-discount-tiers"
              highlightClassName="print-zoom-discount-highlight"
            />
          )}
          <div className="print-zoom-image-wrapper">
            {previewSrc ? (
              <>
                {!imageLoaded && (
                  <div className="print-zoom-spinner">
                    <i className="fa fa-spinner fa-spin" />
                  </div>
                )}
                <img
                  ref={imgRef}
                  src={previewSrc}
                  alt={product.title}
                  className="print-zoom-image"
                  style={imageLoaded ? undefined : { opacity: 0 }}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageLoaded(true)}
                />
              </>
            ) : (
              <div className="print-zoom-image-placeholder">No preview image</div>
            )}
          </div>

          {product.description && (
            <div className="print-zoom-description">
              <p
                className={`print-zoom-desc-text${isDescriptionExpanded ? "" : " description-collapsed"}`}
              >
                {product.description}
              </p>
              {showSeeMore && (
                <button
                  className="toggle-desc-see-more-btn"
                  onClick={onToggleDescription}
                  aria-expanded={isDescriptionExpanded}
                >
                  {isDescriptionExpanded ? "See less" : "See more"}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="print-zoom-disclaimer">
          <strong>Please note: </strong>
          {DISCLAIMER}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PrintProductZoomDrawer;
